<script setup lang="ts">
import { ref, computed, onMounted, useAttrs } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useDismissablePopover } from '@/composables/useDismissablePopover'
import { listEvents } from '@/api/events'
import { useAuth } from '@/composables/useAuth'
import type { Event } from '@/types/events'

const props = defineProps<{
  currentEventSlug: string
}>()

const emit = defineEmits<{
  (e: 'navigate'): void
  (e: 'select', event: Event): void
}>()

const attrs = useAttrs()

const { t } = useI18n()
const router = useRouter()
const route = useRoute()
const { currentOrganisation } = useAuth()

const rootRef = ref<HTMLElement | null>(null)

// Closes on an outside click only (no Escape or focus handling), as the sidebar switchers always did.
const switcher = useDismissablePopover({
  idPrefix: 'event-switcher',
  boundaryRef: rootRef,
  singleOpen: true,
  closeOnOutside: ['click'],
})
const open = switcher.isOpen
const events = ref<Event[]>([])
const loading = ref(false)

const currentEvent = computed(() =>
  events.value.find((e) => e.slug === props.currentEventSlug) ?? null,
)

async function loadEvents(): Promise<void> {
  if (events.value.length > 0 || !currentOrganisation.value) return
  loading.value = true
  try {
    events.value = await listEvents(currentOrganisation.value.id)
  } finally {
    loading.value = false
  }
}

function toggleOpen(): void {
  switcher.toggle()
  if (open.value) void loadEvents()
}

async function selectEvent(event: Event): Promise<void> {
  switcher.close()
  emit('navigate')
  emit('select', event)
  if (!('onSelect' in attrs)) {
    const newPath = route.path.replace(
      `/organizer/events/${props.currentEventSlug}`,
      `/organizer/events/${event.slug}`,
    )
    await router.push(newPath)
  }
}

onMounted(() => {
  void loadEvents()
})

</script>

<template>
  <div ref="rootRef" data-event-switcher class="relative px-3 py-3 border-b border-slate-800">
    <button
      type="button"
      class="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-800/50 hover:bg-slate-800 transition-colors text-left"
      @click="toggleOpen"
    >
      <div class="flex-1 min-w-0">
        <p class="text-xs text-slate-500 font-medium uppercase tracking-wider">
          {{ t('nav.sidebar.event') }}
        </p>
        <p class="text-sm text-white font-semibold truncate mt-0.5">
          {{ currentEvent?.name ?? '...' }}
        </p>
      </div>
      <svg
        class="w-4 h-4 text-slate-400 shrink-0 transition-transform"
        :class="open ? 'rotate-180' : ''"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
      </svg>
    </button>

    <div
      v-if="open"
      class="absolute left-3 right-3 top-full mt-1 z-10 bg-slate-800 border border-slate-700 rounded-lg shadow-xl py-1 max-h-64 overflow-y-auto"
    >
      <div v-if="loading" class="px-4 py-3 text-sm text-slate-400">
        {{ t('common.loading') }}
      </div>
      <button
        v-for="ev in events"
        :key="ev.id"
        type="button"
        class="w-full text-left px-4 py-2.5 text-sm transition-colors flex items-center gap-2"
        :class="ev.slug === currentEventSlug
          ? 'text-white font-semibold bg-slate-700'
          : 'text-slate-300 hover:text-white hover:bg-slate-700/50'"
        @click="selectEvent(ev)"
      >
        <span class="flex-1 truncate">{{ ev.name }}</span>
        <svg
          v-if="ev.slug === currentEventSlug"
          class="w-4 h-4 text-emerald-400 shrink-0"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
        </svg>
      </button>
    </div>
  </div>
</template>
